import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { DeadlineTypeBadge, DeadlineType } from './DeadlineTypeBadge';
import { 
  FileText, 
  MessageSquare, 
  CheckCircle, 
  Award, 
  Clock, 
  Upload, 
  Download,
  ShoppingCart,
  AlertTriangle,
  XCircle,
  FileCheck,
  Settings,
  DollarSign
} from 'lucide-react';

interface ActivityEntry {
  id: string;
  timestamp: Date;
  userRole: 'client' | 'vendor' | 'system';
  userName?: string;
  action: string;
  details?: string;
  icon: React.ReactNode;
  category: string;
}

interface ActivityLogProps {
  jobId: string;
}

export function ActivityLog({ jobId }: ActivityLogProps) {
  const [activities, setActivities] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      if (!jobId) return;
      
      setLoading(true);
      const activityList: ActivityEntry[] = [];

      try {
        // Fetch job data
        const { data: job } = await supabase
          .from('jobs')
          .select('*')
          .eq('id', jobId)
          .single();

        // Fetch delivery details separately (no FK constraint to jobs)
        const { data: deliveryDetailsData } = await supabase
          .from('job_delivery_details')
          .select('*')
          .eq('job_id', jobId)
          .limit(1)
          .maybeSingle();

        if (job) {
          const deliveryDetails = deliveryDetailsData;
          const deadlineType = deliveryDetails?.deadline_type;
          const deadlineTypeLabel = deadlineType === 'hard_deadline' ? 'Hard Deadline' : 
                                   deadlineType === 'flexible_date' ? 'Standard Deadline' : 
                                   'Target Date';
          
          activityList.push({
            id: `job-created-${job.id}`,
            timestamp: new Date(job.created_at),
            userRole: 'client',
            action: 'Job created',
            details: deadlineType ? `${job.title} (${deadlineTypeLabel})` : job.title,
            icon: <FileText className="h-4 w-4" />,
            category: 'job'
          });

          // Fetch detailed job update logs from audit_logs
          const { data: jobUpdateLogs } = await supabase
            .from('audit_logs')
            .select('*')
            .eq('resource_type', 'job')
            .eq('resource_id', jobId)
            .eq('action', 'job_field_updated')
            .order('created_at', { ascending: true });

          jobUpdateLogs?.forEach(log => {
            const changes = (log.details as { changes?: Array<{ fieldLabel: string }> })?.changes || [];
            const changesSummary = changes.map(c => c.fieldLabel).join(', ');

            activityList.push({
              id: `audit-job-update-${log.id}`,
              timestamp: new Date(log.created_at),
              userRole: 'client',
              action: 'Job updated',
              details: changesSummary ? `Updated: ${changesSummary}` : undefined,
              icon: <Settings className="h-4 w-4" />,
              category: 'job'
            });
          });
        }

        // Fetch RFQ data
        const { data: rfqs } = await supabase
          .from('rfqs')
          .select('*')
          .eq('job_id', jobId);

        rfqs?.forEach(rfq => {
          activityList.push({
            id: `rfq-created-${rfq.id}`,
            timestamp: new Date(rfq.created_at),
            userRole: 'client',
            action: 'RFQ created and published',
            details: `Type: ${rfq.visibility_type}`,
            icon: <FileText className="h-4 w-4" />,
            category: 'rfq'
          });

          if (rfq.closes_at && new Date(rfq.closes_at) < new Date() && rfq.status === 'closed') {
            activityList.push({
              id: `rfq-closed-${rfq.id}`,
              timestamp: new Date(rfq.closes_at),
              userRole: 'system',
              action: 'RFQ closed',
              icon: <Clock className="h-4 w-4" />,
              category: 'rfq'
            });
          }
        });

        // Fetch vendor invitations (vendor_user_id is already on this table)
        const { data: invitations } = await supabase
          .from('rfq_vendor_invitations')
          .select('*')
          .in('rfq_id', rfqs?.map(r => r.id) || []);

        invitations?.forEach(inv => {
          activityList.push({
            id: `invitation-${inv.id}`,
            timestamp: new Date(inv.invited_at),
            userRole: 'client',
            action: 'Vendor invited to RFQ',
            icon: <FileText className="h-4 w-4" />,
            category: 'rfq'
          });
        });

        // Fetch bids
        const { data: bids } = await supabase
          .from('bids')
          .select('*')
          .in('rfq_id', rfqs?.map(r => r.id) || []);

        // Note: Bid details now stored in bid_line_items (multi-quantity bidding)
        bids?.forEach(bid => {
          activityList.push({
            id: `bid-${bid.id}`,
            timestamp: new Date(bid.created_at),
            userRole: 'vendor',
            action: 'Bid submitted',
            details: `Bid #${bid.public_id}`,
            icon: <FileCheck className="h-4 w-4" />,
            category: 'bid'
          });
        });

        // Fetch awards - only select columns needed for activity display
        // (prevents exposing total_award_amount_cents to losing vendors via RLS)
        const { data: awards } = await supabase
          .from('awards')
          .select('id, awarded_at')
          .eq('job_id', jobId);

        awards?.forEach(award => {
          activityList.push({
            id: `award-${award.id}`,
            timestamp: new Date(award.awarded_at),
            userRole: 'client',
            action: 'Job awarded',
            icon: <Award className="h-4 w-4" />,
            category: 'award'
          });
        });

        // Fetch purchase orders
        const { data: purchaseOrders } = await supabase
          .from('purchase_orders')
          .select('*')
          .eq('job_id', jobId);

        purchaseOrders?.forEach(po => {
          activityList.push({
            id: `po-created-${po.id}`,
            timestamp: new Date(po.created_at),
            userRole: 'system',
            action: `Purchase Order ${po.po_number} issued to Vendor`,
            icon: <ShoppingCart className="h-4 w-4" />,
            category: 'purchase_order'
          });

          if (po.status === 'current' && po.approved_at) {
            activityList.push({
              id: `po-accepted-${po.id}`,
              timestamp: new Date(po.approved_at),
              userRole: 'vendor',
              action: 'Purchase Order accepted',
              icon: <CheckCircle className="h-4 w-4" />,
              category: 'purchase_order'
            });
          }
        });

        // Fetch financial adjustments (PO modifications)
        const { data: poModifications } = await supabase
          .from('po_modification_requests')
          .select('*, purchase_orders!inner(id, job_id, po_number)')
          .eq('purchase_orders.job_id', jobId)
          .order('created_at', { ascending: true });

        poModifications?.forEach(modification => {
          const isCredit = (modification.adjustment_amount_cents || 0) < 0;
          const isDebit = (modification.adjustment_amount_cents || 0) > 0;
          const amountAbs = Math.abs(modification.adjustment_amount_cents || 0);
          const amountFormatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(amountAbs / 100);
          
          const adjustmentTypeLabel = isCredit ? 'Credit' : 'Debit';
          const adjustmentSymbol = isCredit ? '−' : '+';
          
          // Activity 1: Change Order Requested
          activityList.push({
            id: `adjustment-requested-${modification.id}`,
            timestamp: new Date(modification.created_at),
            userRole: (modification.initiating_party as 'client' | 'vendor') || 'system',
            action: `Change order requested`,
            details: `${adjustmentTypeLabel}: ${adjustmentSymbol}${amountFormatted} - ${modification.adjustment_reason || 'No reason provided'}`,
            icon: <DollarSign className="h-4 w-4" />,
            category: 'financial'
          });

          // Activity 2: Auto-Approved (vendor credits)
          if (modification.auto_approved) {
            activityList.push({
              id: `adjustment-auto-approved-${modification.id}`,
              timestamp: new Date(modification.created_at),
              userRole: 'system',
              action: 'Credit auto-approved and applied',
              details: `${adjustmentSymbol}${amountFormatted} credit applied automatically`,
              icon: <CheckCircle className="h-4 w-4" />,
              category: 'financial'
            });
          }
          
          // Activity 3: Approved (manual approval)
          if (modification.status === 'accepted' && !modification.auto_approved && modification.responded_at) {
            const respondingParty = modification.initiating_party === 'client' ? 'vendor' : 'client';
            
            activityList.push({
              id: `adjustment-approved-${modification.id}`,
              timestamp: new Date(modification.responded_at),
              userRole: respondingParty,
              action: 'Change order approved',
              details: `${adjustmentTypeLabel}: ${adjustmentSymbol}${amountFormatted} approved`,
              icon: <CheckCircle className="h-4 w-4" />,
              category: 'financial'
            });
          }
          
          // Activity 4: Declined
          if (modification.status === 'declined' && modification.responded_at) {
            const respondingParty = modification.initiating_party === 'client' ? 'vendor' : 'client';
            
            activityList.push({
              id: `adjustment-declined-${modification.id}`,
              timestamp: new Date(modification.responded_at),
              userRole: respondingParty,
              action: 'Change order declined',
              details: `${adjustmentTypeLabel}: ${adjustmentSymbol}${amountFormatted} declined`,
              icon: <XCircle className="h-4 w-4" />,
              category: 'financial'
            });
          }
          
          // Activity 5: Applied to PO
          if (modification.applied_at) {
            activityList.push({
              id: `adjustment-applied-${modification.id}`,
              timestamp: new Date(modification.applied_at),
              userRole: 'system',
              action: 'Adjustment applied to Purchase Order',
              details: `PO updated with ${adjustmentTypeLabel.toLowerCase()}: ${adjustmentSymbol}${amountFormatted}`,
              icon: <ShoppingCart className="h-4 w-4" />,
              category: 'financial'
            });
          }
        });

        // Fetch milestones
        const { data: milestones } = await supabase
          .from('milestones')
          .select('*')
          .eq('job_id', jobId)
          .order('created_at', { ascending: true });

        // Only show "timeline submitted" if there are user-created milestones
        const userMilestones = milestones?.filter(m => !m.is_system_milestone) || [];
        if (userMilestones.length > 0) {
          activityList.push({
            id: `milestones-created-${userMilestones[0].id}`,
            timestamp: new Date(userMilestones[0].created_at),
            userRole: 'vendor',
            action: 'Milestone timeline submitted for review',
            icon: <Clock className="h-4 w-4" />,
            category: 'milestone'
          });
        }

        milestones?.forEach(milestone => {
          if (milestone.is_completed && milestone.completed_at) {
            // Determine the actor based on milestone ownership
            let actorRole: 'client' | 'vendor' | 'system' = 'vendor';
            
            if (milestone.is_system_milestone) {
              // System milestones use their action_owner (client owns setup milestones)
              actorRole = (milestone.action_owner as 'client' | 'vendor') || 'client';
            } else {
              // User-created milestones use their action_owner
              actorRole = (milestone.action_owner as 'client' | 'vendor') || 'vendor';
            }
            
            activityList.push({
              id: `milestone-complete-${milestone.id}`,
              timestamp: new Date(milestone.completed_at),
              userRole: actorRole,
              action: `Milestone "${milestone.milestone_name}" marked as complete`,
              icon: <CheckCircle className="h-4 w-4" />,
              category: 'milestone'
            });
          }
        });

        // Fetch milestone approvals
        const { data: approvals } = await supabase
          .from('milestone_approvals')
          .select('*')
          .eq('job_id', jobId);

        approvals?.forEach(approval => {
          if (approval.client_approved_at) {
            activityList.push({
              id: `approval-client-${approval.id}`,
              timestamp: new Date(approval.client_approved_at),
              userRole: 'client',
              action: 'Milestone timeline approved',
              icon: <CheckCircle className="h-4 w-4" />,
              category: 'milestone'
            });
          }
          
          if (approval.vendor_approved_at) {
            activityList.push({
              id: `approval-vendor-${approval.id}`,
              timestamp: new Date(approval.vendor_approved_at),
              userRole: 'vendor',
              action: 'Milestone timeline approved',
              icon: <CheckCircle className="h-4 w-4" />,
              category: 'milestone'
            });
          }
        });

        // Fetch milestone change requests
        const { data: milestoneChangeRequests } = await supabase
          .from('milestone_pending_changes')
          .select(`
            *,
            milestones!inner(id, milestone_name, milestone_type)
          `)
          .eq('job_id', jobId)
          .order('created_at', { ascending: true });

        milestoneChangeRequests?.forEach(changeRequest => {
          const milestoneName = changeRequest.milestones?.milestone_name || 'Unknown milestone';
          const pendingChanges = changeRequest.pending_changes as Record<string, unknown> || {};
          
          // Build a summary of what's being changed
          const fieldLabels: Record<string, string> = {
            due_date: 'Due Date',
            milestone_name: 'Name',
            description: 'Description',
            deliverable: 'Deliverable',
            milestone_type: 'Type',
            action_owner: 'Owner',
            milestone_order: 'Order'
          };
          
          const changedFields = Object.keys(pendingChanges)
            .filter(key => key !== 'change_reason' && fieldLabels[key])
            .map(key => {
              // Format due date changes nicely
              if (key === 'due_date' && pendingChanges.due_date) {
                const newDate = format(new Date(pendingChanges.due_date as string), 'MMM d, yyyy');
                return `Due Date → ${newDate}`;
              }
              return fieldLabels[key] || key;
            });
          
          const changesSummary = changedFields.length > 0 
            ? `Changes to: ${changedFields.join(', ')}` 
            : 'Milestone changes';
          
          const reasonText = changeRequest.change_reason 
            ? ` - "${changeRequest.change_reason}"` 
            : '';

          // Activity 1: Change Requested
          activityList.push({
            id: `milestone-change-requested-${changeRequest.id}`,
            timestamp: new Date(changeRequest.created_at),
            userRole: (changeRequest.requested_by_type as 'client' | 'vendor') || 'vendor',
            action: `Milestone change requested: "${milestoneName}"`,
            details: `${changesSummary}${reasonText}`,
            icon: <Settings className="h-4 w-4" />,
            category: 'milestone'
          });

          // Activity 2: Change Approved
          if (changeRequest.status === 'approved' && changeRequest.responded_at) {
            const respondingParty = changeRequest.requested_by_type === 'client' ? 'vendor' : 'client';
            
            activityList.push({
              id: `milestone-change-approved-${changeRequest.id}`,
              timestamp: new Date(changeRequest.responded_at),
              userRole: respondingParty,
              action: `Milestone change approved: "${milestoneName}"`,
              details: changesSummary,
              icon: <CheckCircle className="h-4 w-4" />,
              category: 'milestone'
            });
          }

          // Activity 3: Change Declined
          if (changeRequest.status === 'declined' && changeRequest.responded_at) {
            const respondingParty = changeRequest.requested_by_type === 'client' ? 'vendor' : 'client';
            
            activityList.push({
              id: `milestone-change-declined-${changeRequest.id}`,
              timestamp: new Date(changeRequest.responded_at),
              userRole: respondingParty,
              action: `Milestone change declined: "${milestoneName}"`,
              details: changesSummary,
              icon: <XCircle className="h-4 w-4" />,
              category: 'milestone'
            });
          }
        });

        // Fetch job files
        const { data: files } = await supabase
          .from('job_files')
          .select('*')
          .eq('job_id', jobId)
          .eq('is_active', true);

        files?.forEach(file => {
          const isProofOfDelivery = file.file_category === 'proof_of_delivery';
          activityList.push({
            id: `file-${file.id}`,
            timestamp: new Date(file.created_at),
            userRole: file.uploader_role as 'client' | 'vendor',
            action: isProofOfDelivery ? 'Proof of delivery submitted' : 'File uploaded',
            details: file.original_filename,
            icon: <Upload className="h-4 w-4" />,
            category: isProofOfDelivery ? 'delivery' : 'file'
          });
        });

        // Fetch job shipments
        const { data: shipments } = await supabase
          .from('job_shipments')
          .select('*')
          .eq('job_id', jobId)
          .order('created_at', { ascending: true });

        shipments?.forEach(shipment => {
          const trackingInfo = Array.isArray(shipment.tracking_numbers) && shipment.tracking_numbers.length > 0
            ? shipment.tracking_numbers.slice(0, 2).join(', ') + (shipment.tracking_numbers.length > 2 ? '...' : '')
            : 'No tracking';
          
          activityList.push({
            id: `shipment-${shipment.id}`,
            timestamp: new Date(shipment.created_at),
            userRole: 'vendor',
            action: 'Shipment added',
            details: `${shipment.carrier_name || 'Unknown carrier'} - ${trackingInfo}`,
            icon: <Download className="h-4 w-4" />,
            category: 'shipment'
          });

          // Add activity for status changes if shipment was updated
          if (shipment.updated_at !== shipment.created_at && shipment.shipment_status !== 'preparing') {
            const statusLabels: Record<string, string> = {
              'preparing': 'Preparing',
              'shipped': 'Shipped',
              'in_transit': 'In Transit',
              'out_for_delivery': 'Out for Delivery',
              'delivered': 'Delivered',
              'exception': 'Exception',
              'cancelled': 'Cancelled'
            };
            
            activityList.push({
              id: `shipment-update-${shipment.id}`,
              timestamp: new Date(shipment.updated_at),
              userRole: 'vendor',
              action: `Shipment status updated to ${statusLabels[shipment.shipment_status] || shipment.shipment_status}`,
              details: shipment.status_notes || undefined,
              icon: <Download className="h-4 w-4" />,
              category: 'shipment'
            });
          }
        });

        // Fetch messages count
        const { count: messageCount } = await supabase
          .from('job_messages')
          .select('*', { count: 'exact', head: true })
          .eq('job_id', jobId);

        if (messageCount && messageCount > 0) {
          // Get first message for timestamp
          const { data: firstMessage } = await supabase
            .from('job_messages')
            .select('created_at')
            .eq('job_id', jobId)
            .order('created_at', { ascending: true })
            .limit(1)
            .single();

          if (firstMessage) {
            activityList.push({
              id: `messages-started`,
              timestamp: new Date(firstMessage.created_at),
              userRole: 'system',
              action: 'Messaging started',
              details: `${messageCount} message${messageCount > 1 ? 's' : ''} exchanged`,
              icon: <MessageSquare className="h-4 w-4" />,
              category: 'message'
            });
          }
        }

        // Fetch job completions
        const { data: completions, error: completionsError } = await supabase
          .from('job_completions')
          .select('*')
          .eq('job_id', jobId);

        if (completionsError) {
          console.error('Error fetching job completions:', completionsError);
        }

        completions?.forEach(completion => {
          // Vendor submits completion
          activityList.push({
            id: `completion-submit-${completion.id}`,
            timestamp: new Date(completion.submitted_at),
            userRole: 'vendor',
            action: 'Job completion submitted',
            details: completion.completion_notes,
            icon: <CheckCircle className="h-4 w-4" />,
            category: 'completion'
          });

          // Client reviews or auto-accept
          if (completion.reviewed_at) {
            const accepted = completion.review_result === 'accepted';
            const isAutoAccept = job?.auto_accepted === true;
            
            // Build details for rejection with reason code
            let reviewDetails = '';
            if (isAutoAccept) {
              reviewDetails = 'No client response within 15 business days - automatically accepted';
            } else if (!accepted && completion.review_reason_code) {
              // Show reason code and notes for rejections
              const reasonCodeLabels: Record<string, string> = {
                'incomplete_work': 'Incomplete Work',
                'quality_issues': 'Quality Issues',
                'missing_requirements': 'Missing Requirements',
                'late_delivery': 'Late Delivery',
                'incorrect_specifications': 'Incorrect Specifications',
                'other': 'Other'
              };
              const reasonLabel = reasonCodeLabels[completion.review_reason_code] || completion.review_reason_code;
              reviewDetails = `Reason: ${reasonLabel}`;
              if (completion.review_notes) {
                reviewDetails += ` - ${completion.review_notes}`;
              }
            } else {
              reviewDetails = completion.review_notes || '';
            }
            
            activityList.push({
              id: `completion-review-${completion.id}`,
              timestamp: new Date(completion.reviewed_at),
              userRole: isAutoAccept ? 'system' : 'client',
              action: accepted 
                ? (isAutoAccept ? 'Job completion auto-accepted' : 'Job completion accepted')
                : 'Job completion rejected',
              details: reviewDetails,
              icon: accepted ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />,
              category: 'completion'
            });
          }
        });

        // Fetch disputes
        const { data: disputes } = await supabase
          .from('disputes')
          .select('*')
          .eq('job_id', jobId);

        disputes?.forEach(dispute => {
          activityList.push({
            id: `dispute-${dispute.id}`,
            timestamp: new Date(dispute.created_at),
            userRole: 'client',
            action: 'Dispute opened',
            details: dispute.reason,
            icon: <AlertTriangle className="h-4 w-4" />,
            category: 'dispute'
          });

          if (dispute.resolved_at) {
            activityList.push({
              id: `dispute-resolved-${dispute.id}`,
              timestamp: new Date(dispute.resolved_at),
              userRole: 'system',
              action: 'Dispute resolved',
              details: dispute.resolution_notes,
              icon: <CheckCircle className="h-4 w-4" />,
              category: 'dispute'
            });
          }
        });

        // Sort all activities by timestamp (most recent first)
        activityList.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

        setActivities(activityList);
      } catch (error) {
        console.error('Error fetching activity log:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [jobId]);

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'client':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Client</Badge>;
      case 'vendor':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Vendor</Badge>;
      case 'system':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Platform System</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Activity Log</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Loading activity log...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity Log</CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[600px] pr-4">
          <div className="space-y-4">
            {activities.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No activities recorded yet</p>
            ) : (
              activities.map((activity) => (
                <div key={activity.id} className="flex gap-4 pb-4 border-b last:border-0">
                  <div className="flex-shrink-0 mt-1">
                    <div className="rounded-full bg-muted p-2">
                      {activity.icon}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm text-muted-foreground">
                        {format(activity.timestamp, 'MMM d, yyyy \'at\' h:mm a')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      {getRoleBadge(activity.userRole)}
                      {activity.userName && (
                        <span className="text-sm text-muted-foreground">- {activity.userName}</span>
                      )}
                    </div>
                    <p className="font-medium">{activity.action}</p>
                    {activity.details && (
                      <p className="text-sm text-muted-foreground mt-1">{activity.details}</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
